import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import SuccessNotification from '../../components/ui/SuccessNotification';
import userService from '../../services/userService';
import { notificationService } from '../../services/notificationService';

const StudentProfileEditor = () => {
  const navigate = useNavigate();
  const { user, userProfile, updateUserProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);

  // Personal Information
  const [personalInfo, setPersonalInfo] = useState({
    fullName: '',
    email: '',
    username: ''
  });

  // Biography
  const [biography, setBiography] = useState('');
  const maxBioLength = 1000;

  // Learning Preferences
  const [learningPreferences, setLearningPreferences] = useState({
    dailyGoalTier: 'casual',
    notificationFrequency: 'daily',
    contentDifficulty: 'intermediate',
    practiceReminders: true
  });

  // Vocal Profile
  const [vocalProfile, setVocalProfile] = useState({
    vocalRange: '',
    preferredGenres: [],
    auditionFocus: []
  });

  // Privacy Settings
  const [privacySettings, setPrivacySettings] = useState({
    profileVisible: true,
    achievementsVisible: true,
    contactVisible: false
  });

  // Notification Preferences
  const [notificationPrefs, setNotificationPrefs] = useState({
    newSongsEnabled: true,
    newLessonsEnabled: true,
    newContentEnabled: true,
    emailEnabled: true
  });

  useEffect(() => {
    if (user && userProfile) {
      setPersonalInfo({
        fullName: userProfile?.full_name || '',
        email: user?.email || '',
        username: userProfile?.username || ''
      });
      setPhotoPreview(userProfile?.profile_photo_url);
      setLearningPreferences(prev => ({
        ...prev,
        dailyGoalTier: userProfile?.daily_goal_tier || 'casual'
      }));
      loadNotificationPreferences();
    }
  }, [user, userProfile]);

  const loadNotificationPreferences = async () => {
    if (!user?.id) return;
    try {
      const prefs = await notificationService?.getPreferences(user?.id);
      setNotificationPrefs(prefs);
    } catch (err) {
      console.error('Error loading notification preferences:', err);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (!file?.type?.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    if (file?.size > 5242880) {
      setError('Image size must be less than 5MB');
      return;
    }

    setIsUploadingPhoto(true);
    setError(null);

    try {
      const { photoUrl, error: uploadError } = await userService?.uploadHeadshot(user?.id, file);

      if (uploadError) {
        throw new Error(uploadError?.message || 'Failed to upload photo');
      }

      setPhotoPreview(photoUrl);
      setSuccessMessage('Profile photo uploaded successfully!');
      setShowSuccess(true);
    } catch (err) {
      console.error('Upload photo error:', err);
      setError(err?.message || 'Failed to upload photo. Please try again.');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleSaveProfile = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!personalInfo?.fullName?.trim()) {
        throw new Error('Full name is required');
      }

      // Update user profile
      const { error: updateError } = await updateUserProfile(user?.id, {
        full_name: personalInfo?.fullName?.trim(),
        username: personalInfo?.username?.trim(),
        daily_goal_tier: learningPreferences?.dailyGoalTier
      });

      if (updateError) {
        throw new Error(updateError?.message || 'Failed to update profile');
      }

      // Update notification preferences
      await notificationService?.updatePreferences(user?.id, notificationPrefs);

      setSuccessMessage('Profile updated successfully!');
      setShowSuccess(true);
    } catch (err) {
      console.error('Save profile error:', err);
      setError(err?.message || 'Failed to save profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenreToggle = (genre) => {
    setVocalProfile(prev => ({
      ...prev,
      preferredGenres: prev?.preferredGenres?.includes(genre)
        ? prev?.preferredGenres?.filter(g => g !== genre)
        : [...prev?.preferredGenres, genre]
    }));
  };

  const handleAuditionFocusToggle = (focus) => {
    setVocalProfile(prev => ({
      ...prev,
      auditionFocus: prev?.auditionFocus?.includes(focus)
        ? prev?.auditionFocus?.filter(f => f !== focus)
        : [...prev?.auditionFocus, focus]
    }));
  };

  const musicGenres = ['Broadway', 'Pop', 'Classical', 'Jazz', 'Rock', 'Contemporary'];
  const auditionFocusAreas = ['Vocal Technique', 'Acting Through Song', 'Dance Integration', 'Character Development', 'Audition Preparation'];

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-4xl">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-8 text-center">
            <Icon name="Lock" size={48} className="text-blue-200 mx-auto mb-4" />
            <h2 className="text-xl font-heading font-semibold text-white mb-2">
              Access Restricted
            </h2>
            <p className="text-blue-100 mb-6">
              Please log in to access your profile editor.
            </p>
            <Button
              variant="default"
              onClick={() => navigate('/login-page')}
              iconName="LogIn"
              iconPosition="left"
              className="bg-white text-indigo-900 hover:bg-gray-100"
            >
              Go to Login
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Student Profile Editor - RoleCall</title>
        <meta name="description" content="Edit your student profile with photo uploads, biography, and learning preferences" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-4xl">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-xl text-blue-200 hover:text-white hover:bg-white hover:bg-opacity-10 transition-colors duration-200"
                aria-label="Go back"
              >
                <Icon name="ArrowLeft" size={24} />
              </button>
              <div>
                <h1 className="text-3xl font-heading font-bold text-white">
                  Student Profile Editor
                </h1>
                <p className="text-blue-100 font-body mt-1">
                  Customize your profile, learning preferences, and vocal profile
                </p>
              </div>
            </div>
          </div>

          {/* Success Notification */}
          {showSuccess && (
            <SuccessNotification
              message={successMessage}
              isVisible={showSuccess}
              onClose={() => setShowSuccess(false)}
            />
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-6 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={20} className="text-red-800" />
                <p className="text-red-800 font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Personal Information Section */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 mb-6">
            <div className="flex items-center space-x-2 mb-6">
              <Icon name="User" size={24} className="text-purple-300" />
              <h2 className="text-2xl font-heading font-bold text-white">
                Personal Information
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Profile Photo Upload */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-white mb-2">
                  Profile Photo
                </label>
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    {photoPreview ? (
                      <img
                        src={photoPreview}
                        alt="Profile preview"
                        className="w-24 h-24 rounded-full object-cover border-4 border-purple-300"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                        <Icon name="User" size={40} className="text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      id="photo-upload"
                      disabled={isUploadingPhoto}
                    />
                    <label htmlFor="photo-upload">
                      <Button
                        as="span"
                        variant="outline"
                        className="bg-white bg-opacity-10 text-white border-white border-opacity-30 hover:bg-opacity-20 cursor-pointer"
                        iconName={isUploadingPhoto ? null : "Upload"}
                        disabled={isUploadingPhoto}
                      >
                        {isUploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                      </Button>
                    </label>
                    <p className="text-xs text-blue-200 mt-2">
                      JPG, PNG or WebP. Max size 5MB.
                    </p>
                  </div>
                </div>
              </div>

              {/* Full Name */}
              <div>
                <Input
                  label="Full Name"
                  type="text"
                  value={personalInfo?.fullName}
                  onChange={(e) => setPersonalInfo(prev => ({ ...prev, fullName: e?.target?.value }))}
                  className="bg-white bg-opacity-10 text-white border-white border-opacity-30 placeholder:text-blue-200"
                  required
                />
              </div>

              {/* Username */}
              <div>
                <Input
                  label="Username"
                  type="text"
                  value={personalInfo?.username}
                  onChange={(e) => setPersonalInfo(prev => ({ ...prev, username: e?.target?.value }))}
                  className="bg-white bg-opacity-10 text-white border-white border-opacity-30 placeholder:text-blue-200"
                />
              </div>

              {/* Email (Read-only) */}
              <div className="md:col-span-2">
                <Input
                  label="Email"
                  type="email"
                  value={personalInfo?.email}
                  disabled
                  className="bg-white bg-opacity-5 text-blue-200 border-white border-opacity-20 cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          {/* Biography Section */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 mb-6">
            <div className="flex items-center space-x-2 mb-6">
              <Icon name="FileText" size={24} className="text-purple-300" />
              <h2 className="text-2xl font-heading font-bold text-white">
                Biography
              </h2>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Tell us about yourself
              </label>
              <textarea
                value={biography}
                onChange={(e) => setBiography(e?.target?.value)}
                maxLength={maxBioLength}
                rows={6}
                className="w-full rounded-xl bg-white bg-opacity-10 text-white border border-white border-opacity-30 px-4 py-3 placeholder:text-blue-200 focus:outline-none focus:ring-2 focus:ring-purple-400"
                placeholder="Share your background, training history, performance experience, and artistic goals..."
              />
              <div className="flex justify-between items-center mt-2">
                <p className="text-xs text-blue-200">
                  Include your training history, performance experience, and artistic goals
                </p>
                <p className="text-xs text-blue-200">
                  {biography?.length}/{maxBioLength}
                </p>
              </div>
            </div>
          </div>

          {/* Learning Preferences Section */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 mb-6">
            <div className="flex items-center space-x-2 mb-6">
              <Icon name="Settings" size={24} className="text-purple-300" />
              <h2 className="text-2xl font-heading font-bold text-white">
                Learning Preferences
              </h2>
            </div>

            <div className="space-y-6">
              {/* Daily Goal Tier */}
              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Daily Goal Tier
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: 'casual', label: 'Casual', xp: '20 XP' },
                    { value: 'regular', label: 'Regular', xp: '40 XP' },
                    { value: 'serious', label: 'Serious', xp: '80 XP' },
                    { value: 'intense', label: 'Intense', xp: '120 XP' }
                  ]?.map((tier) => (
                    <button
                      key={tier?.value}
                      onClick={() => setLearningPreferences(prev => ({ ...prev, dailyGoalTier: tier?.value }))}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        learningPreferences?.dailyGoalTier === tier?.value
                          ? 'bg-purple-600 border-purple-400 text-white' :'bg-white bg-opacity-5 border-white border-opacity-20 text-blue-100 hover:bg-opacity-10'
                      }`}
                    >
                      <div className="font-semibold">{tier?.label}</div>
                      <div className="text-xs mt-1">{tier?.xp}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Notification Frequency */}
              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Notification Frequency
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {['daily', 'weekly', 'never']?.map((freq) => (
                    <button
                      key={freq}
                      onClick={() => setLearningPreferences(prev => ({ ...prev, notificationFrequency: freq }))}
                      className={`p-3 rounded-xl border-2 transition-all capitalize ${
                        learningPreferences?.notificationFrequency === freq
                          ? 'bg-purple-600 border-purple-400 text-white' :'bg-white bg-opacity-5 border-white border-opacity-20 text-blue-100 hover:bg-opacity-10'
                      }`}
                    >
                      {freq}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content Difficulty */}
              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Content Difficulty Level
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {['beginner', 'intermediate', 'advanced']?.map((level) => (
                    <button
                      key={level}
                      onClick={() => setLearningPreferences(prev => ({ ...prev, contentDifficulty: level }))}
                      className={`p-3 rounded-xl border-2 transition-all capitalize ${
                        learningPreferences?.contentDifficulty === level
                          ? 'bg-purple-600 border-purple-400 text-white' :'bg-white bg-opacity-5 border-white border-opacity-20 text-blue-100 hover:bg-opacity-10'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>

              {/* Practice Reminders Toggle */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-white bg-opacity-5 border border-white border-opacity-20">
                <div>
                  <h3 className="text-white font-semibold">Practice Session Reminders</h3>
                  <p className="text-blue-200 text-sm mt-1">Get reminders for your practice sessions</p>
                </div>
                <button
                  onClick={() => setLearningPreferences(prev => ({ ...prev, practiceReminders: !prev?.practiceReminders }))}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    learningPreferences?.practiceReminders ? 'bg-purple-600' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                      learningPreferences?.practiceReminders ? 'translate-x-7' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Notification Preferences */}
              <div className="space-y-3">
                <h3 className="text-white font-semibold mb-3">Email Notifications</h3>
                
                {[
                  { key: 'emailEnabled', label: 'Email Notifications', desc: 'Receive email notifications' },
                  { key: 'newSongsEnabled', label: 'New Songs', desc: 'Get notified about new songs' },
                  { key: 'newLessonsEnabled', label: 'New Lessons', desc: 'Get notified about new lessons' },
                  { key: 'newContentEnabled', label: 'New Content', desc: 'Get notified about new content' }
                ]?.map((pref) => (
                  <div key={pref?.key} className="flex items-center justify-between p-4 rounded-xl bg-white bg-opacity-5 border border-white border-opacity-20">
                    <div>
                      <h4 className="text-white font-medium">{pref?.label}</h4>
                      <p className="text-blue-200 text-sm mt-1">{pref?.desc}</p>
                    </div>
                    <button
                      onClick={() => setNotificationPrefs(prev => ({ ...prev, [pref?.key]: !prev?.[pref?.key] }))}
                      className={`relative w-14 h-7 rounded-full transition-colors ${
                        notificationPrefs?.[pref?.key] ? 'bg-purple-600' : 'bg-gray-600'
                      }`}
                    >
                      <span
                        className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                          notificationPrefs?.[pref?.key] ? 'translate-x-7' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Vocal Profile Section */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 mb-6">
            <div className="flex items-center space-x-2 mb-6">
              <Icon name="Music" size={24} className="text-purple-300" />
              <h2 className="text-2xl font-heading font-bold text-white">
                Vocal Profile
              </h2>
            </div>

            <div className="space-y-6">
              {/* Vocal Range */}
              <div>
                <Input
                  label="Vocal Range"
                  type="text"
                  value={vocalProfile?.vocalRange}
                  onChange={(e) => setVocalProfile(prev => ({ ...prev, vocalRange: e?.target?.value }))}
                  placeholder="e.g., Soprano (C4-C6)"
                  className="bg-white bg-opacity-10 text-white border-white border-opacity-30 placeholder:text-blue-200"
                />
              </div>

              {/* Preferred Musical Genres */}
              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Preferred Musical Genres
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {musicGenres?.map((genre) => (
                    <button
                      key={genre}
                      onClick={() => handleGenreToggle(genre)}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        vocalProfile?.preferredGenres?.includes(genre)
                          ? 'bg-purple-600 border-purple-400 text-white' :'bg-white bg-opacity-5 border-white border-opacity-20 text-blue-100 hover:bg-opacity-10'
                      }`}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
              </div>

              {/* Audition Preparation Focus Areas */}
              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Audition Preparation Focus Areas
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {auditionFocusAreas?.map((focus) => (
                    <button
                      key={focus}
                      onClick={() => handleAuditionFocusToggle(focus)}
                      className={`p-3 rounded-xl border-2 transition-all text-left ${
                        vocalProfile?.auditionFocus?.includes(focus)
                          ? 'bg-purple-600 border-purple-400 text-white' :'bg-white bg-opacity-5 border-white border-opacity-20 text-blue-100 hover:bg-opacity-10'
                      }`}
                    >
                      {focus}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Privacy Controls Section */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 mb-6">
            <div className="flex items-center space-x-2 mb-6">
              <Icon name="Shield" size={24} className="text-purple-300" />
              <h2 className="text-2xl font-heading font-bold text-white">
                Privacy Controls
              </h2>
            </div>

            <div className="space-y-3">
              {[
                { key: 'profileVisible', label: 'Profile Visibility', desc: 'Make your profile visible to other users' },
                { key: 'achievementsVisible', label: 'Achievement Sharing', desc: 'Share your achievements and badges' },
                { key: 'contactVisible', label: 'Contact Information', desc: 'Display contact information on profile' }
              ]?.map((setting) => (
                <div key={setting?.key} className="flex items-center justify-between p-4 rounded-xl bg-white bg-opacity-5 border border-white border-opacity-20">
                  <div>
                    <h3 className="text-white font-semibold">{setting?.label}</h3>
                    <p className="text-blue-200 text-sm mt-1">{setting?.desc}</p>
                  </div>
                  <button
                    onClick={() => setPrivacySettings(prev => ({ ...prev, [setting?.key]: !prev?.[setting?.key] }))}
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      privacySettings?.[setting?.key] ? 'bg-purple-600' : 'bg-gray-600'
                    }`}
                  >
                    <span
                      className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                        privacySettings?.[setting?.key] ? 'translate-x-7' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="bg-white bg-opacity-10 text-white border-white border-opacity-30 hover:bg-opacity-20"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSaveProfile}
              disabled={isLoading}
              iconName={isLoading ? null : "Save"}
              className="bg-purple-600 text-white hover:bg-purple-700"
            >
              {isLoading ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>
        </main>
      </div>
    </>
  );
};

export default StudentProfileEditor;